"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { cn } from "@/utilities/core/cn";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import type { TeamLogoDisplay } from "@/lib/tenant/TenantSettingsProvider";
import { TeamShield } from "./TeamShield";
import { PlayerAvatar } from "./PlayerAvatar";
type TeamLogoSize = "xs" | "sm" | "md" | "lg" | "xl" | "xl2" | "xl3";
const SIZE_MAP: Record<TeamLogoSize, number> = {
  xs: 16,
  sm: 20,
  md: 32,
  lg: 48,
  xl: 64,
  xl2: 80,
  xl3: 96
};
interface TeamLogoProps {
  src?: string | null;
  alt?: string;
  size?: TeamLogoSize;
  className?: string;
  /** Team's primary brand color (for shield mode) */
  primaryColor?: string | null;
  /** Team's secondary brand color (for shield mode) */
  secondaryColor?: string | null;
  /** Team's country code, e.g. "GB" (for flag mode) */
  countryCode?: string | null;
  /** Team's country flag URL from API (for flag mode, takes precedence over countryCode) */
  countryFlag?: string | null;
  /**
   * "competition": always renders the real logo regardless of tenant settings.
   * "player": uses a person-shaped avatar instead of a team shield as fallback (e.g. tennis players).
   * Default: "team".
   */
  kind?: "team" | "competition" | "player";
  /**
   * Native FansUnited competition ID (e.g. "fb:c:30"). When provided alongside
   * `kind="competition"`, a per-tenant logo override may replace `src`.
   */
  competitionId?: string | null;
}
function buildFlagUrl(countryFlag?: string | null, countryCode?: string | null): string | null {
  return countryFlag || (countryCode ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png` : null);
}
export function TeamLogo(t0) {
  const $ = _c(47);
  const {
    src,
    alt: t1,
    size: t2,
    className,
    primaryColor,
    secondaryColor,
    countryCode,
    countryFlag,
    kind: t3
  } = t0;
  const alt = t1 === undefined ? "" : t1;
  const size = t2 === undefined ? "md" : t2;
  const kind = t3 === undefined ? "team" : t3;
  const {
    teamLogoDisplay
  } = useTenantSettings();
  const px = SIZE_MAP[size];
  const mode = kind === "competition" ? "logos" : teamLogoDisplay;
  const resolvedSrc = src;
  let t4;
  if ($[0] !== className || $[1] !== kind || $[2] !== primaryColor || $[3] !== secondaryColor || $[4] !== size) {
    t4 = t5 => {
      const withColors = t5 === undefined ? true : t5;
      return kind === "player" ? <PlayerAvatar primaryColor={withColors ? primaryColor : null} size={size} className={className} /> : <TeamShield primaryColor={withColors ? primaryColor : null} secondaryColor={withColors ? secondaryColor : null} size={size} className={className} />;
    };
    $[0] = className;
    $[1] = kind;
    $[2] = primaryColor;
    $[3] = secondaryColor;
    $[4] = size;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const renderFallback = t4;
  let t5;
  if ($[6] !== alt || $[7] !== className || $[8] !== px) {
    t5 = imgUrl => <div className={cn("relative shrink-0 overflow-hidden rounded-full", className)} style={{
      width: px,
      height: px
    }}><Image src={imgUrl} alt={alt} fill={true} sizes={`${px}px`} className="object-cover" unoptimized={true} /></div>;
    $[6] = alt;
    $[7] = className;
    $[8] = px;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const renderCircularAvatar = t5;
  let t6;
  if ($[10] !== alt || $[11] !== className || $[12] !== kind || $[13] !== px || $[14] !== renderCircularAvatar) {
    t6 = flagUrl => {
      if (kind === "competition") {
        return <Image src={flagUrl} alt={alt} width={px} height={px} className={cn("object-contain shrink-0", className)} unoptimized={true} />;
      }
      return renderCircularAvatar(flagUrl);
    };
    $[10] = alt;
    $[11] = className;
    $[12] = kind;
    $[13] = px;
    $[14] = renderCircularAvatar;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const renderFlag = t6;
  if (mode === "shield-fallback") {
    let t7;
    if ($[16] !== renderFallback) {
      t7 = renderFallback();
      $[16] = renderFallback;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  if (mode === "country-flags") {
    let t7;
    if ($[18] !== countryCode || $[19] !== countryFlag || $[20] !== renderFlag) {
      t7 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const flagUrl_0 = buildFlagUrl(countryFlag, countryCode);
        if (flagUrl_0) {
          t7 = renderFlag(flagUrl_0);
          break bb0;
        }
      }
      $[18] = countryCode;
      $[19] = countryFlag;
      $[20] = renderFlag;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    if (t7 !== Symbol.for("react.early_return_sentinel")) {
      return t7;
    }
    let t8;
    if ($[22] !== renderFallback) {
      t8 = renderFallback(false);
      $[22] = renderFallback;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    return t8;
  }
  if (!resolvedSrc) {
    let t7;
    if ($[24] !== countryCode || $[25] !== countryFlag || $[26] !== renderFlag) {
      t7 = Symbol.for("react.early_return_sentinel");
      bb1: {
        const flagUrl_1 = buildFlagUrl(countryFlag, countryCode);
        if (flagUrl_1) {
          t7 = renderFlag(flagUrl_1);
          break bb1;
        }
      }
      $[24] = countryCode;
      $[25] = countryFlag;
      $[26] = renderFlag;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    if (t7 !== Symbol.for("react.early_return_sentinel")) {
      return t7;
    }
    let t8;
    if ($[28] !== renderFallback) {
      t8 = renderFallback(false);
      $[28] = renderFallback;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    return t8;
  }
  let t7;
  let urlReplaced;
  if ($[30] !== kind || $[31] !== renderCircularAvatar || $[32] !== resolvedSrc) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb2: {
      urlReplaced = resolvedSrc.replace("/process/assets", "/process/smp-images-production/assets");
      if (kind === "player") {
        t7 = renderCircularAvatar(urlReplaced);
        break bb2;
      }
    }
    $[30] = kind;
    $[31] = renderCircularAvatar;
    $[32] = resolvedSrc;
    $[33] = t7;
    $[34] = urlReplaced;
  } else {
    t7 = $[33];
    urlReplaced = $[34];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[35] !== className) {
    t8 = cn("relative shrink-0", className);
    $[35] = className;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== px) {
    t9 = {
      width: px,
      height: px
    };
    $[37] = px;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const t10 = `${px}px`;
  let t11;
  if ($[39] !== alt || $[40] !== t10 || $[41] !== urlReplaced) {
    t11 = <Image src={urlReplaced} alt={alt} fill={true} sizes={t10} className="object-contain" unoptimized={true} />;
    $[39] = alt;
    $[40] = t10;
    $[41] = urlReplaced;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== t11 || $[44] !== t8 || $[45] !== t9) {
    t12 = <div className={t8} style={t9}>{t11}</div>;
    $[43] = t11;
    $[44] = t8;
    $[45] = t9;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
}

/**
 * Resolves the image URL for a team logo based on the tenant's display mode.
 *
 * Use this for library components that only accept a URL string (e.g. MatchScore,
 * FormIndicator, MatchList). When the display mode is "logos" the original logo URL
 * is returned; for "country-flags" a flag CDN URL is produced; for
 * "shield-fallback" an empty string is returned (the library component will show
 * its own fallback).
 */
export function resolveTeamLogoUrl(mode: TeamLogoDisplay, opts: {
  logo?: string | null;
  countryFlag?: string | null;
  countryCode?: string | null;
}): string {
  if (mode === "country-flags") {
    return buildFlagUrl(opts.countryFlag, opts.countryCode) || "";
  }
  if (mode === "shield-fallback") {
    // No meaningful URL for shield mode — library will show its built-in fallback
    return "";
  }

  // "logos" mode (default): real logo → country flag → empty (library shows its own fallback)
  if (opts.logo) {
    return opts.logo.replace("/process/assets", "/process/smp-images-production/assets");
  }
  return buildFlagUrl(opts.countryFlag, opts.countryCode) || "";
}

/**
 * React hook that returns a resolver bound to the current tenant's display mode.
 */
export function useTeamLogoUrl() {
  const $ = _c(2);
  const {
    teamLogoDisplay
  } = useTenantSettings();
  let t0;
  if ($[0] !== teamLogoDisplay) {
    t0 = opts => resolveTeamLogoUrl(teamLogoDisplay, opts);
    $[0] = teamLogoDisplay;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}